<template>
  <Dialog @keydown.esc.native="handleCloseDialog()">
    <div class="flex flex-col w-full h-full">
      <div id="header">
        <div class="flex justify-between items-center pl-5 pr-4 pt-3.5">
          <div class="flex items-center space-x-5">
            <div class="flex items-center space-x-2 font-manrope cursor-default">
              <img :src="`/static/roundtable_icons/${'written'}_icon_color.svg`" class="w-4 h-4" />
              <span>Written RoundTable</span>
            </div>
            <button v-if="isLoading === false && course" class="flex items-center space-x-2.5 font-manrope text-gray-800">
              <div class="flex items-center space-x-2.5 hover:underline">
                <img src="/static/roundtable_icons/courses_icon.svg" class=" w-3 -mt-0.5" />
                <span >{{ course.title }}</span>
              
              </div>
            </button>
          </div>
          <div class="flex items-center space-x-3">
            <div v-if="isSaving" class="flex items-center space-x-2">
              <div class="flex items-center justify-center">
                <img src="/static/roundtable_icons/cloud-upload.svg" class="w-4 h-4"/>
              </div>
              <span class="font-manrope text-xs text-gray-800">Saving Changes...</span>
            </div>
            <button @click="handleCloseDialog()" class="w-7 h-7 focus:ring-2 focus:ring-offset-2 focus:ring-primary-dark rounded-full cursor-pointer bg-gray-50 hover:bg-gray-100 text-base flex items-center justify-center hover:text-gray-800 text-gray-700">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
        <div class="flex justify-between space-x-5 items-center px-5 relative w-full mb-2.5 mt-2.5">
          <div class="flex-grow">
            <input
              v-model="title"
              placeholder="Enter a title..."
              class="w-full text-3xl font-manrope font-bold text-gray-800 border-none bg-white py-1 outline-none"
            />
          </div>
          <div class="flex items-center space-x-2">
            <button @click="handleSaveRoundTable()" :disabled="isSaveButtonDisabled" class="flex sample items-center space-x-2 disabled:opacity-50 bg-primary hover:bg-primary-dark rounded-md border border-solid border-primary text-white py-1.5 px-4">
              <div v-if="isSaving === true" class="loader -ml-1"></div>
              <span>{{ isSaving === true ? 'Saving...' : 'Save' }}</span>
            </button>
          </div>
        </div>
        <div class="flex items-center justify-between w-full h-11 focus-within:border-pinky border-b border-solid border-gray-200 pl-4 pr-5">
          <div class="flex items-center w-full font-manrope">
            <div class="w-14 h-14 -ml-3">
              <iframe src="https://lottie.host/embed/874b8bf3-ca7e-46f3-9c81-cf94974ad30b/Djg5qZsQK9.lottie" width="100%" height="100%"></iframe>
            </div>
            <input 
              ref="genieRevise" 
              @keyup.enter="handleReviseWrittenPrompt()" 
              v-model="revise.input" placeholder="How can I help you make this discussion prompt better?" 
              class="w-full disabled:bg-white border-none"
            />
          </div>
          <div class="flex items-center space-x-5">
            <pl-menu direction="right" :menuSize="3">
              <button slot="menu_trigger" class="flex items-center space-x-2.5 font-manrope text-gray-800">
                <span>{{ revise.mode }}</span>
                <i class="fa-solid fa-chevron-down text-xs text-gray-600 hover:text-gray-800"></i>
              </button>
              <ul class="flex flex-col space-y-1.5 rounded-lg mt-0.5 text-sm shadow-sm px-1 pt-0.5 pb-1 font-manrope max-h-96" menu-content>
                <li class="flex items-center space-x-1 justify-between hover:bg-gray-100 px-2.5 py-1 cursor-pointer rounded-md">
                  <span>Prompt</span>
                  <i v-if="revise.mode === 'Prompt'" class="fa-solid fa-check text-gray-400"></i>
                </li>
                <li class="flex justify-between space-x-1 text-gray-400 cursor-not-allowed items-center hover:bg-gray-100 px-2.5 py-1 rounded-md">
                  <span>Selection</span>
                  <i v-if="revise.mode === 'Selection'" class="fa-solid fa-check text-gray-400"></i>
                </li>
              </ul>
            </pl-menu>
            <button @click="handleReviseWrittenPrompt()" :disabled="revise.input === '' || revise.input.length < 4 ? true : false" class="text-white w-8 h-6 rounded-lg flex justify-center items-center disabled:opacity-50 bg-pinky hover:bg-pinky-dark cursor-pointer">
              <i class="fa-solid fa-arrow-right-long"></i>
            </button>
          </div>
        </div>
        <div v-if="isRevising === true" class='w-full'>
          <div class='h-0.5 w-full bg-pink-100 overflow-hidden'>
            <div class='progress w-full h-full bg-pink-500 left-right'></div>
          </div>
        </div>
      </div>
      <div v-if="isLoading === false && roundtable" class="w-full h-full flex-grow">
        <Editor
          :init="editor"
          api-key="no-api-key"
          v-model="content"
          ref="editorWrittenPrompt"
        />
      </div>
      <div v-else class="w-full h-full flex-grow">
        <div role="status" class="space-y-2.5 animate-pulse w-full px-16 py-14">
          <div class="flex items-center w-full space-x-2">
            <div class="h-3 bg-gray-200 rounded-full dark:bg-gray-700 w-32"></div>
            <div class="h-3 bg-gray-300 rounded-full dark:bg-gray-600 w-24"></div>
            <div class="h-3 bg-gray-300 rounded-full dark:bg-gray-600 w-full"></div>
          </div>
          <div class="flex items-center w-full space-x-2">
            <div class="h-3 bg-gray-200 rounded-full dark:bg-gray-700 w-full"></div>
            <div class="h-3 bg-gray-300 rounded-full dark:bg-gray-600 w-full"></div>
            <div class="h-3 bg-gray-300 rounded-full dark:bg-gray-600 w-24"></div>
          </div>
          <div class="flex items-center w-full space-x-2">
            <div class="h-3 bg-gray-300 rounded-full dark:bg-gray-600 w-full"></div>
            <div class="h-3 bg-gray-200 rounded-full dark:bg-gray-700 w-80"></div>
            <div class="h-3 bg-gray-300 rounded-full dark:bg-gray-600 w-full"></div>
          </div>
          <div class="flex items-center w-full space-x-2">
            <div class="h-3 bg-gray-200 rounded-full dark:bg-gray-700 w-full"></div>
            <div class="h-3 bg-gray-300 rounded-full dark:bg-gray-600 w-full"></div>
            <div class="h-3 bg-gray-300 rounded-full dark:bg-gray-600 w-24"></div>
          </div>
          <div class="flex items-center w-full space-x-2">
            <div class="h-3 bg-gray-300 rounded-full dark:bg-gray-600 w-32"></div>
            <div class="h-3 bg-gray-300 rounded-full dark:bg-gray-600 w-24"></div>
            <div class="h-3 bg-gray-200 rounded-full dark:bg-gray-700 w-full"></div>
          </div>
          <div class="flex items-center w-full space-x-2">
            <div class="h-3 bg-gray-300 rounded-full dark:bg-gray-600 w-full"></div>
            <div class="h-3 bg-gray-200 rounded-full dark:bg-gray-700 w-80"></div>
            <div class="h-3 bg-gray-300 rounded-full dark:bg-gray-600 w-full"></div>
          </div>
          <div class="flex items-center w-full space-x-2">
            <div class="h-3 bg-gray-200 rounded-full dark:bg-gray-700 w-32"></div>
            <div class="h-3 bg-gray-300 rounded-full dark:bg-gray-600 w-24"></div>
            <div class="h-3 bg-gray-300 rounded-full dark:bg-gray-600 w-full"></div>
          </div>
          <div class="flex items-center w-full space-x-2">
            <div class="h-3 bg-gray-200 rounded-full dark:bg-gray-700 w-full"></div>
            <div class="h-3 bg-gray-300 rounded-full dark:bg-gray-600 w-full"></div>
            <div class="h-3 bg-gray-300 rounded-full dark:bg-gray-600 w-24"></div>
          </div>
          <div class="flex items-center w-full space-x-2">
            <div class="h-3 bg-gray-300 rounded-full dark:bg-gray-600 w-full"></div>
            <div class="h-3 bg-gray-200 rounded-full dark:bg-gray-700 w-80"></div>
            <div class="h-3 bg-gray-300 rounded-full dark:bg-gray-600 w-full"></div>
          </div>
          <div class="flex items-center w-full space-x-2">
            <div class="h-3 bg-gray-200 rounded-full dark:bg-gray-700 w-full"></div>
            <div class="h-3 bg-gray-300 rounded-full dark:bg-gray-600 w-full"></div>
            <div class="h-3 bg-gray-300 rounded-full dark:bg-gray-600 w-24"></div>
          </div>
          <div class="flex items-center w-full space-x-2">
            <div class="h-3 bg-gray-300 rounded-full dark:bg-gray-600 w-32"></div>
            <div class="h-3 bg-gray-300 rounded-full dark:bg-gray-600 w-24"></div>
            <div class="h-3 bg-gray-200 rounded-full dark:bg-gray-700 w-full"></div>
          </div>
          <div class="flex items-center w-full space-x-2">
            <div class="h-3 bg-gray-300 rounded-full dark:bg-gray-600 w-full"></div>
            <div class="h-3 bg-gray-200 rounded-full dark:bg-gray-700 w-80"></div>
            <div class="h-3 bg-gray-300 rounded-full dark:bg-gray-600 w-full"></div>
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>
<script>
  import Axios from 'axios';
  import Dialog from '../../../components/Dialogs/Dialog.vue';
  import Editor from '@tinymce/tinymce-vue';
  import { EditorOptions } from '../../Drafts/EditorOptions';
  import { mapGetters } from 'vuex';
  import { EventBus } from '../../../utils/event-bus';
  export default {
    name: "EditWrittenRoundTable",
    components: {
      Dialog,
      Editor,
    },
    props: {
      id: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        title: "",
        revise: {
          input: "",
          mode: "Prompt"
        },
        isSaving: false,
        isLoading: false,
        isRevising: false,
        editor: EditorOptions,
        roundtable: null,
        selection: "",
        course: null,
        content: "",
      };
    },
    computed: {
      ...mapGetters(['getProfile', 'getCurrentRT', 'getCourseDetail', 'getCourses', 'getRecentRtsList']),
      isSaveButtonDisabled() {
        if (!this.getCurrentRT || !this.content) {
          return true;
        }
        if (this.getCurrentRT.prompt === this.content && this.getCurrentRT.title === this.title) {
          return true;
        }
        return false;
      }
    },
    methods: {
      handleCloseDialog: async function() {
        this.$emit('close');
      },
      handleSaveRoundTable: async function() {
        
        try {
          this.isSaving = true;

          const htmlString = this.content;

          const parser = new DOMParser();
          const doc = parser.parseFromString(htmlString, 'text/html');
          const ulElement = doc.getElementById('peer-questions');


          let liTexts = [];
          if (ulElement) {
            const liElements = ulElement.querySelectorAll('li');
            liElements.forEach(li => {
              const text = li.textContent.trim();
              if (text) {
                liTexts.push(text);
              }
            });
          }

          const currentQuestions = this.getCurrentRT.peer_assessment_questions;

          let updatedPeerQuestions = [];

          currentQuestions.forEach(q => {
            if (!q.is_custom) {
              updatedPeerQuestions.push({
                ...q,
                is_active: liTexts.includes(q.question)
              });
            } else {
              if (liTexts.includes(q.question)) {
                updatedPeerQuestions.push({
                  ...q,
                  is_active: true 
                });
              }
            }
          });

          liTexts.forEach(text => {
            const exists = updatedPeerQuestions.some(q => q.question === text);
            if (!exists) {
              updatedPeerQuestions.push({
                question: text,
                is_active: true,
                is_custom: false
              });
            }
          });

          const payload = {
            id: this.roundtable.id,
            title: this.title,
            prompt: this.content,
            peer_assessment_questions: updatedPeerQuestions
          };
          await this.$store.dispatch('savingRTDetails', payload);
          this.isSaving = false;
          this.$emit('close');
          EventBus.$emit('createToast', {type: "Success", message: "Written RoundTable updated successfully!", time: 5000 });
        } catch (error) {
          console.error(error);
          EventBus.$emit('createToast', {type: "Error", message: "Something failing updating discussion, please try again!", time: 5000 });
        }
      },
      handleGetData: async function() {
        this.isLoading = true;
        try {
          if (this.$route.name === 'dashboard') {
            this.title = this.getRecentRtsList.find(rt => rt.id === this.id).title;
          }
          if (this.getCurrentRT.id !== this.id) {
            await this.$store.dispatch('fetchRoundtable', this.id);
          }
          const roundtable = this.getCurrentRT;
          this.roundtable = { ...roundtable };
          this.title = roundtable.title;
          this.course = this.getCourses.find(course => course.id === roundtable.CourseId);
          this.content = roundtable.prompt;
        } catch (error) {
          console.error(error);
        } finally {
          this.isLoading = false;
        }
      },
      handleReviseWrittenPrompt: async function() {
        try {
          if (this.isRevising === true) {
            return;
          }
          this.isRevising = true;
          const payload = {
            input: this.revise.input,
            prompt: this.roundtable.prompt,
            mode: this.revise.mode,
            selection: this.selection
          };          
          this.revise.input = 'Thinking...';
          this.$refs.genieRevise.blur();
          const response = await Axios.post(`/api/genie/v1/revise/written/prompt`, payload);
          const editor = this.$refs.editorWrittenPrompt.editor;
          if (editor) {
            editor.execCommand('SelectAll');
            editor.execCommand('mceInsertContent', false, response.data.revise);
          }
          this.roundtable.prompt = response.data.revise;
          this.isRevising = false;
          this.revise.input = '';
        } catch (error) {
          console.error(error);
        }
      },
    },
    mounted: async function() {
      await this.handleGetData();


      this.$nextTick(() => {
        const editorInstance = this.$refs.editorWrittenPrompt.editor;
        if (editorInstance) {
          editorInstance.options.set('disabled', false);
        }
      });

      EventBus.$on("handleSelection", (data) => {
        this.selection = data.selected;
        this.revise.mode = data.mode;
      });

      EventBus.$on('handleRevisePrompt', () => {
        this.$refs.genieRevise.focus();
      });
    }
  }; 
</script>

<style scoped>

  .progress {
    animation: progress 1s infinite linear;
  }

  .left-right {
    transform-origin: 0% 50%;
  }
  @keyframes progress {
    0% {
      transform:  translateX(0) scaleX(0);
    }
    40% {
      transform:  translateX(0) scaleX(0.4);
    }
    100% {
      transform:  translateX(100%) scaleX(0.5);
    }
  }
  .loader,
  .loader:after {
    border-radius: 50%;
    width: 16px;
    height: 16px;
    margin-top: -1px;
  }
  .loader {
    border-top: 3px solid rgba(255, 255, 255, 0.2);
    border-right: 3px solid rgba(255, 255, 255, 0.2);
    border-bottom: 3px solid rgba(255, 255, 255, 0.2);
    border-left: 3px solid #ffffff;
    -webkit-transform: translateZ(0);
    -ms-transform: translateZ(0);
    transform: translateZ(0);
    -webkit-animation: load8 1.1s infinite linear;
    animation: load8 1.1s infinite linear;
  }
  @-webkit-keyframes load8 {
    0% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
    }
    100% {
      -webkit-transform: rotate(360deg);
      transform: rotate(360deg);
    }
  }
  @keyframes load8 {
    0% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
    }
    100% {
      -webkit-transform: rotate(360deg);
      transform: rotate(360deg);
    }
  }
</style>


// WEBPACK FOOTER //
// EditWrittenRoundTable.vue?2a418059